import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import EnvironmentService from '@/env/EnvironmentService';
import { OneOffQuizCategory, QuizCategory } from '@/models/Quiz';
import type User from '@/models/user/User';
import { Routes } from '@/router/Routes';

/**
 * Prezentacja oświadczeń jednokrotnych. Treść oświadczenia przychodzi z BE (`GET /quiz/:category`
 * — całość siedzi w `text` jedynego pytania i jest wersjonowana), więc tutaj trzymamy tylko opakowanie:
 * nazwę na kafelku, ilustrację i warunki dostępu. Kategoria bez wpisu w `oneOffStatementsConfig`
 * nie jest renderowana, żeby nowa kategoria z BE nie pokazała pustej karty.
 */
export interface OneOffStatementConfig {
  /** Zapas na wypadek braku `displayName` z `GET /quiz` — nazwa na kafelku i w nagłówku widoku. */
  title: string;
  /** Krótki opis na kafelku w zakładce Oświadczenia. */
  description: string;
  /** Dłuższy opis w nagłówku widoku oświadczenia — dopowiada, czego dotyczy akredytacja. */
  viewDescription: string;
  /** Szacowany czas wypełnienia — pokazywany na karcie przed akceptacją. */
  duration: string;
  tooltipDescription: string;
  /** Plik z `@/assets/icons/quizes`. Dla oświadczeń bankowych ilustracja bez glifu banku (nadkłada się logo). */
  icon: string;
  bankSymbol?: BankSymbol;
  routeName: string;
  /** Instrukcja od instytucji do pobrania z widoku oświadczenia; `null` gdy brak pliku. */
  instructionFileUrl: Nullable<string>;
  /** Nazwa, pod jaką plik ma się zapisać u eksperta. */
  instructionFileName?: string;
  /** Czy oświadczenie jest w ogóle widoczne dla danego użytkownika. */
  isAvailable: (user: Nullable<User>) => boolean;
}

const UNI_CREDIT_STATEMENT: OneOffStatementConfig = {
  // Nagłówek widoku oświadczenia wg projektu. Na kafelku wygrywa `displayName` z BE
  // („Oświadczenie UniCredit"), bo tam nazwa ma wskazywać na samo oświadczenie.
  title: 'Akredytacja UniCredit',
  description: 'Dobrowolna akredytacja umożliwiająca ofertowanie produktów UniCredit.',
  viewDescription: 'Dobrowolna akredytacja umożliwiająca ofertowanie produktów UniCredit. '
    + 'Jej warunkiem jest złożenie oświadczenia o zasadach korzystania z konta Google '
    + 'utworzonego na służbowym adresie e-mail w domenie Lendi.',
  duration: 'Zajmie 10 minut',
  tooltipDescription: 'Oświadczenie jednorazowe — nie wymaga cyklicznego odnawiania, a jego złożenie jest dobrowolne. '
    + 'Bez zaakceptowania oświadczenia nie będzie możliwe ofertowanie produktów UniCredit.',
  icon: 'bank_statements_no_bank.svg',
  bankSymbol: BankSymbol.UNICREDIT,
  routeName: Routes.STATEMENTS_TEST.UNI_CREDIT_STATEMENT,
  // AC#20: instrukcja założenia konta Google od UniCredit (statyczny plik, ten sam host co
  // materiały do oświadczeń akredytacyjnych).
  instructionFileUrl: 'https://static.lendi.pl/shared/Instrukcja%20za%C5%82o%C5%BCenia%20konta%20Google.pdf',
  instructionFileName: 'Instrukcja założenia konta Google.pdf',
  /**
   * AC#3: kafel dostają wyłącznie eksperci z akredytacją gotówkową LUB hipoteczną.
   * `isAppDomain().LENDI_PL` to tenant (lendi.pl vs imobiliare.ro), a NIE organizacja — bez
   * filtra po `isUserOf` oświadczenie pokazywało się też ekspertom Finpacka. Akredytacja
   * otworzyła temat dla Finamaxu na tych samych warunkach co Lendi (09.2026); BE nie
   * rozróżnia organizacji, więc to jedyny gate.
   */
  isAvailable: (user: Nullable<User>) => {
    const privileges = user?.privileges;
    return !!user && !!privileges &&
      EnvironmentService.Environment.isAppDomain().LENDI_PL &&
      (user.isUserOf.LENDI || user.isUserOf.FINAMAX) &&
      (!privileges.mortgageLoansDisabled || !privileges.cashLoansDisabled);
  },
};

export const oneOffStatementsConfig: Record<OneOffQuizCategory, OneOffStatementConfig> = {
  [QuizCategory.UNI_CREDIT]: UNI_CREDIT_STATEMENT,
};

export const getOneOffStatementConfig = (category: QuizCategory): OneOffStatementConfig | undefined =>
  oneOffStatementsConfig[category as OneOffQuizCategory];
